/**
 * The one piece of state the table keeps for itself: what the player is
 * holding — picked up and not yet put down. A tray whose markers are being
 * arranged, cards chosen for a ship, a card chosen for the discard, a tile
 * waiting for its slot. Nothing here is sent until it is complete, and a new
 * view clears it.
 */
import {
  MARKERS_PER_TRAY,
  decisionMaker,
  type Color,
  type PlayerId,
  type TrayIndex,
  type View,
} from '@trajan/engine';
import { move, swap, swapMarkers } from '../mat/placement';
import { EMPTY_SELECTION, type Selection } from '../panel/seaport';
import { emptyPlan, type TilePlan } from '../setup/tilePlan';

export type Holding =
  | { kind: 'none' }
  /** A tray picked up: its markers laid into the trays they fall into, in this order. */
  | { kind: 'tray'; tray: TrayIndex; order: Color[]; held: number | null }
  /** Setup: the twelve markers, two per tray, being arranged. */
  | { kind: 'arrange'; layout: Color[][]; held: number | null }
  /** Setup: the three starting Trajan tiles being put on slots. */
  | { kind: 'tiles'; plan: TilePlan }
  /** Seaport: cards (and wild tiles) chosen for a display or a ship; `wildFor` awaits its commodity. */
  | { kind: 'cards'; selection: Selection; wildFor: string | null }
  /** After drawing: the card chosen to discard, waiting for a pile. */
  | { kind: 'discard'; cardId: string }
  /** A "+2" tile being accomplished, waiting for the space its marker goes on. */
  | { kind: 'plus2' }
  /** A wild extra-action tile chosen, waiting for the action it repeats. */
  | { kind: 'wild'; tileId: string };

export const NONE: Holding = { kind: 'none' };

/**
 * What the player may hold in this phase. Some phases open with something in
 * hand — the markers to arrange, the starting tiles to place — and anything
 * left over from another phase, or from someone else's turn, is dropped rather
 * than carried along.
 */
export function settle(view: View, seat: PlayerId, holding: Holding): Holding {
  const player = view.players[seat];
  if (!player || decisionMaker(view) !== seat) return NONE;
  const phase = view.phase;
  switch (phase.kind) {
    case 'setup.arrangeMarkers':
      return holding.kind === 'arrange'
        ? holding
        : { kind: 'arrange', layout: player.trays.map((t) => [...t]), held: null };
    case 'setup.chooseTrajanTiles':
      return holding.kind === 'tiles' ? holding : { kind: 'tiles', plan: emptyPlan() };
    case 'awaitTray':
      return holding.kind === 'tray' && (player.trays[holding.tray]?.length ?? 0) > 0
        ? holding
        : NONE;
    case 'awaitTrajanDecision':
      return holding.kind === 'plus2' && player.trajanSlots[phase.slot]?.kind === 'plus2'
        ? holding
        : NONE;
    case 'awaitAction':
      return phase.action === 'seaport' && holding.kind === 'cards' ? holding : NONE;
    case 'awaitSeaportDiscard':
      return holding.kind === 'discard' && (player.hand ?? []).some((c) => c.id === holding.cardId)
        ? holding
        : NONE;
    case 'awaitExtraAction':
      return holding.kind === 'wild' && player.forumTiles.some((t) => t.id === holding.tileId)
        ? holding
        : NONE;
    default:
      return NONE;
  }
}

/** Pick a tray up, with its markers in the order they lie. */
export const pickTray = (view: View, seat: PlayerId, tray: TrayIndex): Holding => ({
  kind: 'tray',
  tray,
  order: [...(view.players[seat]?.trays[tray] ?? [])],
  held: null,
});

/** Cards in hand for the seaport, none chosen yet unless given. */
export const holdCards = (selection: Selection = EMPTY_SELECTION): Holding => ({
  kind: 'cards',
  selection,
  wildFor: null,
});

/** Exchange two markers, in whichever arrangement is open. */
export function swapIn(holding: Holding, a: number, b: number): Holding {
  if (holding.kind === 'tray') return { ...holding, order: swap(holding.order, a, b), held: null };
  if (holding.kind === 'arrange') {
    return { ...holding, layout: swapMarkers(holding.layout, a, b, MARKERS_PER_TRAY), held: null };
  }
  return holding;
}

/**
 * A marker was tapped: lift it, put it back down, or — with another already
 * lifted — swap the two. Tapping is how a marker moves without a drag, and how
 * the keyboard moves one (Enter or Space on its button).
 */
export function tapMarker(holding: Holding, index: number): Holding {
  if (holding.kind !== 'tray' && holding.kind !== 'arrange') return holding;
  if (holding.held === null) return { ...holding, held: index };
  if (holding.held === index) return { ...holding, held: null };
  return swapIn(holding, holding.held, index);
}

/** The arrow keys: move a marker one place along the order of the tray being placed. */
export function nudgeMarker(
  holding: Holding,
  index: number,
  step: -1 | 1,
): { holding: Holding; index: number } {
  if (holding.kind !== 'tray') return { holding, index };
  const to = index + step;
  if (to < 0 || to >= holding.order.length) return { holding, index };
  return { holding: { ...holding, order: move(holding.order, index, to), held: to }, index: to };
}

/** The colour of a marker in an open arrangement, for drawing it under a drag. */
export function markerColour(holding: Holding, index: number): Color | null {
  if (holding.kind === 'tray') return holding.order[index] ?? null;
  if (holding.kind === 'arrange') {
    return holding.layout[Math.floor(index / MARKERS_PER_TRAY)]?.[index % MARKERS_PER_TRAY] ?? null;
  }
  return null;
}
